import { useEffect, useState } from "react";
import { Link } from "react-router";

import classes from "./Microneedling.module.css";
import { treatmentPages, microneedlingServices } from "./serviceData";
import { packagesData } from "../Packages/packagesData";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
} from "../../utils/tracking";

import heroImage from "../../Assets/micro-top.jpg";
import ledImage from "../../Assets/dermalux_treatment.jpg";
import microImage from "../../Assets/Micro.jpg";
import raffineImage from "../../Assets/raffine-micro-needling-device.jpeg";

const page = treatmentPages.find((p) => p.slug === "microneedling");
const facialService = microneedlingServices.find(
  (s) => s.id === "microneedling-facial",
);
const addOnServices = microneedlingServices.filter(
  (s) => s.id !== "microneedling-facial",
);
const microneedlingPackages = packagesData.filter((pkg) =>
  pkg.id.startsWith("microneedling"),
);

const benefits = [
  {
    icon: "✨",
    title: "Fine Lines & Wrinkles",
    text: "Boosts collagen and elastin to soften early signs of ageing.",
  },
  {
    icon: "🎯",
    title: "Acne Scarring",
    text: "Smooths texture and refines the appearance of scarring and imperfections.",
  },
  {
    icon: "🔬",
    title: "Enlarged Pores",
    text: "Encourages a refined, more even skin surface over time.",
  },
  {
    icon: "🌟",
    title: "Uneven Tone & Texture",
    text: "Rejuvenates dull, uneven skin from within for a brighter complexion.",
  },
];

const journeySteps = [
  {
    title: "Double Cleanse, Steam & Hot Towel Wraps",
    text: "Your treatment begins with a thorough double cleanse and gentle steam to remove impurities and soften the pores, with hot towel wraps used throughout for comfort and relaxation.",
  },
  {
    title: "Targeted Exfoliation",
    text: "A targeted exfoliation refines the skin's surface, preparing it to get the most out of the microneedling step to follow.",
  },
  {
    title: "Microneedling with the Medical-Grade Raffine Device",
    text: "Precise micro-channels are created within the skin using the innovative Raffine device, stimulating your body's natural healing response and boosting collagen and elastin production. A targeted serum, chosen for your skin type and goals, is infused during treatment for deep absorption.",
  },
  {
    title: "Bio-Cellulose Mask & Chest Massage",
    text: "A soothing bio-cellulose mask deeply hydrates and calms the skin, while a relaxing chest massage enhances the overall experience.",
  },
  {
    title: "Dermalux LED Light Therapy",
    text: "Included in every session, Dermalux LED light therapy works to reduce redness, support healing, and further stimulate collagen production for enhanced results.",
  },
  {
    title: "Ice Globe Massage & Vitamins",
    text: "Your treatment finishes with a refreshing ice globe massage to soothe and tighten the skin, alongside vitamins and minerals to nourish, protect and leave your complexion glowing.",
  },
];

const deviceStats = [
  {
    label: "9 Surgical-Grade Needles",
    text: "Ultra-fine, 33-gauge surgical stainless steel needles create precise micro-channels with minimal trauma to the skin.",
  },
  {
    label: "0.1mm – 2.0mm Depth",
    text: "Fully adjustable needle depth, precisely tailored to your skin concern and the treatment area.",
  },
  {
    label: "~960 Insertions/Second",
    text: "A high-speed BLDC motor delivers fast, even coverage for a more comfortable, consistent treatment.",
  },
  {
    label: "Single-Use Sterile Cartridges",
    text: "Every client receives a brand-new, sealed sterile cartridge — never reused, for guaranteed hygiene and safety.",
  },
];

const expectTimeline = [
  {
    when: "24–72 Hours",
    text: "Mild redness and tightness, similar to a light sunburn — a normal and expected part of your skin's healing response.",
  },
  {
    when: "1–2 Weeks",
    text: "An initial glow and smoother-feeling texture as the skin's surface renews.",
  },
  {
    when: "8–12 Weeks",
    text: "Continued collagen production brings the most visible improvement in fine lines, scarring and pore size.",
  },
];

const aftercareBullets = [
  "No exercising or sweating for 12 hours after treatment",
  "Avoid swimming, saunas or similar spa environments for 3 days",
  "Sleep on a clean, fresh pillowcase — silk if you have one",
  "Avoid retinoids, glycolic acid, salicylic acid, scrubs and exfoliators for 3–5 days",
  "Wait 24 hours before applying makeup, and use clean, sanitised brushes",
  "Leave at least 4 weeks between microneedling sessions",
];

const faqs = [
  {
    q: "Is this just microneedling, or a full facial?",
    a: "It's a full facial built around the microneedling step. Every appointment includes a double cleanse, steam, hot towel wraps, exfoliation, a bio-cellulose mask with chest massage, Dermalux LED light therapy, and a finishing ice globe massage — all included in your 1-hour session.",
  },
  {
    q: "Is microneedling painful?",
    a: "Most clients describe it as a mild tingling sensation. A topical numbing cream is applied beforehand to ensure your comfort. Any redness typically subsides within 24–48 hours.",
  },
  {
    q: "How many sessions will I need?",
    a: "Many clients notice an initial glow after a single session, but for concerns like acne scarring, texture or fine lines, a course of 3–6 sessions spaced 4–6 weeks apart is usually recommended for the best results, with a top-up once or twice a year to maintain them.",
  },
  {
    q: "Who shouldn't have microneedling?",
    a: "Microneedling isn't suitable for everyone — for example if you're pregnant, have active acne or a skin infection, or are taking certain blood-thinning medications. Your consultation will confirm you're a good candidate before any treatment begins.",
  },
];

const MicroneedlingPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Microneedling FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>Nurse-Led · Medical-Grade</p>
            <h1 className={classes.heroTitle}>Microneedling Facial</h1>
            <p className={classes.heroMeta}>
              <span>From £{facialService.price}</span> ·{" "}
              {facialService.duration}
            </p>
            <p className={classes.heroDescription}>
              Medical-grade collagen induction therapy for fine lines, acne
              scarring, enlarged pores and uneven texture — delivered as a full,
              deeply relaxing facial experience, not just the needling.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Microneedling Hero")}
            >
              Book Now
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Client receiving a microneedling facial at Radiance Aesthetics Wigan"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Highlight banner */}
      <section className={classes.section}>
        <div className={classes.highlightBannerWrapper}>
          <div className={classes.highlightBanner}>
            <h2 className={classes.highlightTitle}>
              More Than Just Microneedling
            </h2>
            <p className={classes.highlightText}>
              Every Microneedling Facial here is a complete facial experience
              built around the needling step — not just the needling itself.
              Your session includes a <strong>double cleanse and steam</strong>,{" "}
              <strong>hot towel wraps</strong>, <strong>exfoliation</strong>,
              the <strong>medical-grade Raffine</strong> microneedling
              treatment, a{" "}
              <strong>bio-cellulose mask with chest massage</strong>,{" "}
              <strong>Dermalux LED light therapy</strong>, and a finishing{" "}
              <strong>ice globe massage with vitamins and minerals</strong> —
              all included in your appointment.
            </p>
          </div>
        </div>
      </section>

      {/* What it treats */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>What It Treats</p>
          <h2 className={classes.sectionTitle}>Suitable For</h2>
          <p className={classes.sectionDescription}>
            Microneedling is highly effective for a range of common skin
            concerns, suitable for most skin types.
          </p>
          <div className={classes.benefitGrid}>
            {benefits.map((b) => (
              <div className={classes.benefitCard} key={b.title}>
                <div className={classes.benefitIcon} aria-hidden="true">
                  {b.icon}
                </div>
                <h3 className={classes.benefitTitle}>{b.title}</h3>
                <p className={classes.benefitText}>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Device spotlight */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <div className={classes.deviceCard}>
            <span className={classes.deviceBadge}>
              CE Certified · Class IIB Medical Device
            </span>
            <div className={classes.deviceIntro}>
              <div className={classes.deviceImageWrapper}>
                <img
                  className={classes.deviceImage}
                  src={raffineImage}
                  alt="The Raffine medical-grade microneedling device used at Radiance Aesthetics"
                  loading="lazy"
                />
              </div>
              <div className={classes.deviceIntroContent}>
                <p className={classes.sectionLabel}>The Technology</p>
                <h2 className={classes.deviceTitle}>
                  Powered by the Raffine Device
                </h2>
                <p className={classes.deviceText}>
                  At the heart of every treatment is the Raffine — a
                  CE-certified, Class IIB{" "}
                  <strong>medical-grade microneedling system</strong>, the
                  same standard of device relied on in clinical settings. Its
                  precision engineering delivers controlled, consistent
                  results you simply won't get from at-home or beauty-grade
                  tools.
                </p>
              </div>
            </div>
            <div className={classes.deviceStatsGrid}>
              {deviceStats.map((stat) => (
                <div className={classes.deviceStat} key={stat.label}>
                  <p className={classes.deviceStatLabel}>{stat.label}</p>
                  <p className={classes.deviceStatText}>{stat.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Step By Step</p>
          <h2 className={classes.sectionTitle}>Your Treatment Journey</h2>
          <p className={classes.sectionDescription}>
            Every Microneedling Facial follows the same relaxing, thorough
            process from start to finish.
          </p>
          <div className={classes.journeyList}>
            {journeySteps.map((step, i) => (
              <div className={classes.journeyStep} key={step.title}>
                <div className={classes.journeyNumber}>{i + 1}</div>
                <div className={classes.journeyContent}>
                  <h3 className={classes.journeyTitle}>{step.title}</h3>
                  <p className={classes.journeyText}>{step.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className={classes.journeyImages}>
            <img
              className={classes.journeyImage}
              src={microImage}
              alt="Nurse performing a facial treatment step at Radiance Aesthetics"
              loading="lazy"
            />
            <img
              className={classes.journeyImage}
              src={ledImage}
              alt="Client under the Dermalux LED light therapy device included in every microneedling facial"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Downtime & Results</p>
          <h2 className={classes.sectionTitle}>What to Expect</h2>
          <p className={classes.sectionDescription}>
            This treatment delivers smoother, firmer, more radiant skin with
            minimal downtime. You can expect an initial glow within days, with
            continued improvement as collagen production increases over time.
          </p>
          <div className={classes.expectGrid}>
            {expectTimeline.map((t) => (
              <div className={classes.expectCard} key={t.when}>
                <p className={classes.expectWhen}>{t.when}</p>
                <p className={classes.expectText}>{t.text}</p>
              </div>
            ))}
          </div>
          <p className={classes.expectNote}>
            Individual results vary. For deeper concerns like acne scarring, a
            course of treatments spaced 4–6 weeks apart is recommended — see
            course pricing below.
          </p>
        </div>
      </section>

      {/* Book this treatment */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Book This Treatment</p>
          <h2 className={classes.sectionTitle}>{facialService.name}</h2>
          <div className={classes.serviceCard}>
            <p className={classes.servicePrice}>£{facialService.price}</p>
            <p className={classes.serviceDuration}>
              {facialService.duration} · Full facial included
            </p>
            <ul className={classes.serviceHighlights}>
              <li>Medical-grade Raffine microneedling device</li>
              <li>Targeted serum infused for your skin's needs</li>
              <li>Bio-cellulose mask, chest massage & Dermalux LED</li>
              <li>Finished with an ice globe massage and vitamins</li>
            </ul>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Microneedling Service Card")}
            >
              Book This Treatment
            </a>
          </div>
        </div>
      </section>

      {/* Packages */}
      {microneedlingPackages.length > 0 && (
        <section className={`${classes.section} ${classes.sectionAlt}`}>
          <div className={classes.sectionContainer}>
            <p className={classes.sectionLabel}>Save With a Course</p>
            <h2 className={classes.sectionTitle}>Course Pricing</h2>
            <p className={classes.sectionDescription}>
              Committing to a course is the best way to see results for
              scarring, texture and fine lines.
            </p>
            <div className={classes.packageGrid}>
              {microneedlingPackages.map((pkg) => (
                <div className={classes.packageCard} key={pkg.id}>
                  <p className={classes.packageSessions}>
                    {pkg.sessions} sessions
                  </p>
                  <div className={classes.packagePriceRow}>
                    <span className={classes.packagePrice}>£{pkg.price}</span>
                    <span className={classes.packageRrp}>£{pkg.rrp}</span>
                  </div>
                  <span className={classes.packageSave}>
                    Save £{pkg.rrp - pkg.price}
                  </span>
                </div>
              ))}
            </div>
            <Link to="/packages" className={classes.packageLink}>
              View All Packages →
            </Link>
          </div>
        </section>
      )}

      {/* Add-ons */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Add-Ons</p>
          <h2 className={classes.sectionTitle}>Extend Your Treatment</h2>
          <p className={classes.sectionDescription}>
            Add extra coverage on to your Microneedling Facial.
          </p>
          <div className={classes.addonGrid}>
            {addOnServices.map((addon) => (
              <div className={classes.addonCard} key={addon.id}>
                <div className={classes.addonHeader}>
                  <span className={classes.addonName}>{addon.name}</span>
                  <span className={classes.addonPrice}>
                    £{addon.price} · {addon.duration}
                  </span>
                </div>
                <p className={classes.addonText}>{addon.description}</p>
              </div>
            ))}
          </div>
          <p className={classes.addonNote}>
            Add-ons are booked alongside your Microneedling Facial — mention it
            when booking or during your consultation.
          </p>
        </div>
      </section>

      {/* Aftercare */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Aftercare</p>
          <h2 className={classes.sectionTitle}>Caring For Your Skin After</h2>
          <div className={classes.aftercareCard}>
            <ul className={classes.aftercareList}>
              {aftercareBullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Link to="/aftercare" className={classes.aftercareLink}>
              Read Full Aftercare Guide →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={classes.section} ref={faqSectionRef}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready to Start Your Journey?</h2>
        <p className={classes.ctaDescription}>
          Book your Microneedling Facial today, or get in touch for a free
          consultation to discuss your skin goals.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Microneedling Bottom CTA")}
        >
          Book Now
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Microneedling" />
      </div>
    </>
  );
};

export default MicroneedlingPage;
